import { useRef } from "react";

import { UserTypeAssignmentPopup } from "@/Administration/Components/Users/UserTypeAssignmentPopup";
import { EmployeeAsyncActions } from "@/Common/Api/AsyncActions/EmployeeAsyncActions";
import { LoginMultifactorAuthenticationController } from "@/Common/Components/MultifactorAuthentication/LoginMultifactorAuthenticationController";
import { ChangePasswordPopupControl } from "@/Common/Components/Password/ChangePasswordPopupControl";
import { RequiredRecoveryEmailController } from "@/Common/Components/RequiredRecoveryEmailFlow/RequiredRecoveryEmailController";
import { ILoginModel } from "@/Common/Models/UI/Login/ILoginModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { LoginWorkflow } from "@/Common/Workflow/LoginWorkflow/LoginWorkflow";
import { LoginWorkflowStepEnum } from "@/Common/Workflow/LoginWorkflow/LoginWorkflowStepEnum";
import { SessionStore } from "@/Globals/Stores/SessionStore";
import { AcceptAgreementPopup } from "./AcceptAgreementPopup";

interface ILoginWorkflowPopupsProps {
    stringProvider: ResourceProvider;
    loginWorkflow: LoginWorkflow;
    loginModel: ILoginModel;
    password: string;
    onPasswordChange?: (newPassword: string) => void;
}

function LoginWorkflowPopups(props: ILoginWorkflowPopupsProps): JSX.Element {
    const employeeActions = useRef(new EmployeeAsyncActions());
    const currentStep = props.loginWorkflow.getCurrentStep();

    if (!currentStep)
        return null;

    switch (currentStep.stepEnum) {
        case LoginWorkflowStepEnum.ChangeTempPassword:
            return (
                <ChangePasswordPopupControl
                    popupTitle="tempPasswordPopupTitle"
                    stringProvider={props.stringProvider}
                    isAdminReset={false}
                    userHasAdminPermission={sessionStore?.isAdmin()}
                    onClosePopupCallBack={() => sessionStore.cancelWorkflowStep(LoginWorkflowStepEnum.ChangeTempPassword)}
                    onSuccess={newPassword => {
                        sessionStore.isTempPassword = false;
                        props.onPasswordChange(newPassword);
                        sessionStore.completeWorkflowStep(LoginWorkflowStepEnum.ChangeTempPassword);
                    }}
                    isNewAdminUser={props.loginModel.get("IsNewAdminUser")}
                    customPasswordRuleMessage={sessionStore.customPasswordRuleMessage}
                    customAdminPasswordRuleMessage={sessionStore.customAdminPasswordRuleMessage}
                    customPasswordValidationRegex={sessionStore.customPasswordValidationRegex}
                />
            );

        case LoginWorkflowStepEnum.AcceptAgreement:
            return (
                <AcceptAgreementPopup
                    onContinue={() => {
                        const checked = props.loginModel.get("AcceptAgreementChecked");
                        if (checked) {
                            sessionStore.dispatch(LocalOperations.Login.AcceptAgreement);
                        }
                    }}
                    stringProvider={props.stringProvider}
                    checked={props.loginModel.get("AcceptAgreementChecked")}
                    inProgress={props.loginModel.get("AcceptingAgreementInProgress")}
                />
            );

        case LoginWorkflowStepEnum.UserTypeAssignment:
            return (
                <UserTypeAssignmentPopup
                    editedUser={currentStep.stepEnum == LoginWorkflowStepEnum.UserTypeAssignment ? sessionStore.getCurrentEmployee() : null}
                    onCancel={() => sessionStore.cancelWorkflowStep(LoginWorkflowStepEnum.UserTypeAssignment)}
                    onContinue={editedUser => sessionStore.action.saveUserType(editedUser.ContactFacetId, editedUser.ContactId, editedUser.getIssuingOrgRule()?.Id, editedUser.Model.LicenseOrganizationSpecializationId)}
                    popupTitle={cacheStore.GetString("InformationRequiredLabel")}
                    popupMessage={cacheStore.GetString("UserTypeNotAssignedLabel")}
                />
            );

        case LoginWorkflowStepEnum.RequiredRecoveryEmailFlow:
            return (
                <RequiredRecoveryEmailController
                    isForLogin
                    sendSaveEmailRequest={options => employeeActions.current.addEmail(options)}
                    onVerifyEmailSuccess={verifiedEmail => {
                        const onBeforeComplete = (store: SessionStore): void => {
                            store.isRequiredEmailFlowEnded = true;
                            store.unverifiedEmail = null;
                            store.lastVerifiedEmail = verifiedEmail;
                        };
                        sessionStore.completeWorkflowStep(LoginWorkflowStepEnum.RequiredRecoveryEmailFlow, onBeforeComplete);
                    }}
                />
            );

        case LoginWorkflowStepEnum.RequiredMandatoryMultiFactorAuthenticationSetUpFlow:
            return (
                <LoginMultifactorAuthenticationController
                    password={props.password}
                    verifiedEmail={sessionStore.lastVerifiedEmail}
                />
            );

        default:
            return null;
    }
}

export { LoginWorkflowPopups };
